import { translate } from "@/config/translator";
import {
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
} from "@fluentui/react-components";
import { faSearch } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HeaderProps } from "./header/Header.types";
import { useHeader } from "./header/useHeader";

export default function Header({
  text,
  children,
  onMainButtonClick,
  mainButtonText,
  onSecondaryButtonClick,
  secondaryButtonText,
  searchValue,
  onChangeSearch,
  filterOptions,
  filterOptionsGroups,
  setFilters,
  filterValues,
  mainButtonDisabled,
  secondaryButtonDisabled,
  secondaryText,
  rightSide,
}: HeaderProps) {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang, onChangeSearchWrapper, onChangeFiltersWrapper } = useHeader({
    filterValues,
    onChangeSearch,
    setFilters,
  });

  // -----------------------------------------------------------------------------
  // CALLBACK FUNCTIONS
  // -----------------------------------------------------------------------------

  return (
    <div className="mb-5 flex-row items-center justify-between hidden tablet:flex">
      {/* HEADER TEXT */}
      <div className="flex flex-col">
        <Text size={600} weight="semibold">
          {text ? translate(text, lang) : children}
        </Text>
        {secondaryText && (
          <Text size={300} className="text-font-secondary">
            {secondaryText}
          </Text>
        )}
      </div>
      {/* ACTIONS NEXT TO HEADER */}
      <div className="flex flex-row gap-4">
        {/* FILTERS */}
        {setFilters && (filterOptions || filterOptionsGroups) && (
          <Popover size="large">
            {/* OPEN FILTERS BUTTON */}
            <PopoverTrigger>
              <Button>{translate("filters", lang)}</Button>
            </PopoverTrigger>

            {/* FILTERS CONTENT */}
            <PopoverSurface>
              {filterOptions ? (
                <div className="flex flex-col gap-2 h-[50vh] overflow-y-scroll">
                  {filterOptions.map((option) => (
                    <Checkbox
                      onChange={onChangeFiltersWrapper}
                      checked={filterValues?.includes(option.key)}
                      label={option.label}
                      value={option.key}
                      key={`filter-option-${option.key}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-6 h-[50vh] overflow-y-scroll">
                  {/*
                    the '!' is because for this to exist, either
                    filterOptions or filterOptionsGroups must be truthy
                  */}
                  {filterOptionsGroups!.map((optionsGroup) => (
                    <div
                      className="flex flex-col gap-2"
                      key={`filter-option-group-${optionsGroup.groupLabel}`}
                    >
                      <div className="text-font-secondary font-medium text-lg">
                        {optionsGroup.groupLabel}
                      </div>
                      {optionsGroup.groupOptions.map((option) => (
                        <Checkbox
                          key={`filter-option-${option.key}`}
                          onChange={onChangeFiltersWrapper}
                          checked={filterValues?.includes(option.key)}
                          label={option.label}
                          value={option.key}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </PopoverSurface>
          </Popover>
        )}

        {/* SEARCH CONTENTS */}
        {onChangeSearch && searchValue !== undefined && (
          <Input
            type="text"
            size="large"
            value={searchValue}
            onChange={onChangeSearchWrapper}
            contentBefore={<FontAwesomeIcon icon={faSearch} />}
          />
        )}
        {secondaryButtonText && onSecondaryButtonClick && (
          <Button
            disabled={secondaryButtonDisabled}
            size="large"
            appearance="secondary"
            onClick={onSecondaryButtonClick}
          >
            {translate(secondaryButtonText, lang)}
          </Button>
        )}
        {mainButtonText && onMainButtonClick && (
          <Button
            disabled={mainButtonDisabled}
            size="large"
            appearance="primary"
            onClick={onMainButtonClick}
          >
            {translate(mainButtonText, lang)}
          </Button>
        )}
        {rightSide}
      </div>
    </div>
  );
}
